/**
 * Send on the "Send a payment link" step, made safe to press again.
 *
 * Send is two calls: create the Stripe link, then send it. When the create worked and the send did
 * not (a dropped connection, a 500, a 504), pressing Send again used to create a second link and a
 * second payment row on the case (review of #140, Oct 2026). This keeps the link that was made and
 * sends that one on a retry.
 *
 * If staff change the amount after a link was made, that link is cancelled before a new one is made
 * (the Payments tab's cancel, which deactivates it in Stripe and keeps the row as cancelled). It was
 * never confirmed sent, and leaving it live would leave a wrong-price link on the case that anyone
 * could later send or pay. If the cancel fails, nothing new is made.
 *
 * A second press while one is still running gets the same answer, so a double click makes one link.
 */

export interface CreatedLink {
  TicketPayment_guid: string;
  TicketPayment_AmountCents: number;
}

/** What the Payments tab can say about a link as it is made; passed to create unchanged. */
export interface PaymentLinkCreateOptions {
  isEscalationFee?: boolean;
  passThrough?: { passThroughBaseCents: number };
  feeKind?: string | null;
}

export interface PaymentLinkSendApi<R> {
  create(
    ticketGuid: string,
    amountCents: number,
    note?: string,
    options?: PaymentLinkCreateOptions,
  ): Promise<CreatedLink>;
  send(ticketGuid: string, paymentGuid: string, channels: { email: boolean; sms: boolean }): Promise<R>;
  cancel(ticketGuid: string, paymentGuid: string, reason: string): Promise<unknown>;
}

export interface PaymentLinkSendRequest {
  ticketGuid: string;
  amountCents: number;
  note?: string;
  channels: { email: boolean; sms: boolean };
  create?: PaymentLinkCreateOptions;
}

export interface PaymentLinkSender<R> {
  send(request: PaymentLinkSendRequest): Promise<R>;
  /** The link made on an earlier try and not yet sent, if any. */
  pendingLink(): CreatedLink | null;
  /**
   * The last link made or sent, kept after a send that answered: a send that reached nobody (no
   * email on file, the mail server down) still answers, and a retry should send this same link.
   */
  lastLink(): CreatedLink | null;
}

export const AMOUNT_CHANGED_REASON = 'Amount changed before sending';

/**
 * `startWith` is a link already made and not yet sent, such as one Create case made when its send
 * failed: the next Send sends that link rather than making another.
 */
export function createPaymentLinkSender<R>(
  api: PaymentLinkSendApi<R>,
  startWith: CreatedLink | null = null,
): PaymentLinkSender<R> {
  let link: CreatedLink | null = startWith;
  let last: CreatedLink | null = startWith;
  let inFlight: Promise<R> | null = null;

  const run = async (request: PaymentLinkSendRequest): Promise<R> => {
    if (link && link.TicketPayment_AmountCents !== request.amountCents) {
      await api.cancel(request.ticketGuid, link.TicketPayment_guid, AMOUNT_CHANGED_REASON);
      link = null;
    }
    if (!link) {
      link = await api.create(request.ticketGuid, request.amountCents, request.note, request.create);
      last = link;
    }
    const result = await api.send(request.ticketGuid, link.TicketPayment_guid, request.channels);
    link = null;
    return result;
  };

  return {
    send(request) {
      if (!inFlight) {
        inFlight = run(request).finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    },
    pendingLink: () => link,
    lastLink: () => last,
  };
}
